import * as React from 'react';
import axios from 'axios';
import { useEffect, useState } from 'react';
import qs from 'qs';
import { useQueryParam, StringParam, BooleanParam } from 'use-query-params';
import useDebounce from '../../components/utilities/debouncer';

import { StatusCounts, Order } from '../types';
import { ButtonStyles, CardLayout, HomeScreen, OuterWrap } from './styles';
import Button from '../../components/Button';
import { useUserContext } from '../../context/UserContext';
import { useSearchParams } from '../../context/SearchParamsContext';

import ClientOrders from './ClientOrders';
import TopBar from './TopBar';
import Search from '../Home/Search';
import Card from './Card';

type FilterType = 'In Progress' | 'Partially Completed' | 'Waiting on Client' | 'Completed' | '';

const baseFilter = {
  pageNumber: 1,
  sortBy: 'lastUpdated',
  statusFilter: '' as FilterType,
  isSearching: true,
  isFiltering: true
}

const HomePage = () => {
  const userContext = useUserContext();
  const searchParams = useSearchParams();
  const clientId = userContext.user.clientId;

  const [clientOrders, setClientOrders] = useState<Order[]>([]);
  const [legacyOrders, setLegacyOrders] = useState<Order[]>([]);
  const [lastLegacyDate, setLastLegacyDate] = useState<Date>();

  const [filters, setFilters] = useState(baseFilter);
  const debouncedSearch = useDebounce(searchParams.mainSearchText, 200);
  const [searchLegacy, setSearchLegacy] = useState<boolean>(false);

  const [filterCount, setCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<StatusCounts>();
  const [legacyCount, setLegacyCount] = useState(0);
  const [legacyTotal, setLegacyTotal] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    const filter = (sessionStorage.getItem('filter') || baseFilter.statusFilter) as FilterType;
    if (filter)
    {
      setSearchFilterState(filter, false, true);
    }
    document.title = "Hudson Search";
    if (clientId !== 0) {
      getStaticData(filter);
    }
  }, [clientId]);

  useEffect(() => {
    const filter = (sessionStorage.getItem('filter') || baseFilter.statusFilter) as FilterType;
    if (clientId !== 0 && searchParams.mainSearchText) {
      setSearchFilterState(filter, true, false);
      Promise.all([
        getClientOrdersCount(searchParams.mainSearchText, filter),
        getClientOrders(searchParams.mainSearchText, filter, baseFilter.pageNumber, baseFilter.sortBy)
      ])
    }
    if (clientId !== 0 && !searchParams.mainSearchText && searchParams.topSearchText) {
      setSearchFilterState(filter, baseFilter.isSearching, baseFilter.isFiltering);
      Promise.all([
        getClientOrdersCount(searchParams.topSearchText, filter),
        getClientOrders(searchParams.topSearchText, filter, baseFilter.pageNumber, baseFilter.sortBy)
      ])
    }
    if (clientId != 0 &&  !searchParams.mainSearchText && !searchParams.topSearchText) {
      setSearchFilterState(filter, baseFilter.isSearching, baseFilter.isFiltering);
      Promise.all([
        getClientOrdersCount('', filter),
        getClientOrders('', filter, baseFilter.pageNumber, baseFilter.sortBy)
      ])
    }
  }, [clientId, debouncedSearch])

  const getStaticData = async (filter: FilterType = filters.statusFilter) => {
    await Promise.all([
      getStatusCounts(),
      getLegacyOrdersCount(searchParams.topSearchText ? searchParams.topSearchText : searchParams.mainSearchText),
      getLastLegacyDate()
    ]);
    if (searchParams.topSearchText || searchParams.mainSearchText) {
      setSearchFilterState(baseFilter.statusFilter, true, false);
    }
  }

  const getClientOrders = async (
    searchText: string,
    statusFilter: string,
    pageNumber: number,
    sortBy: string) => {
    const queryString = qs.stringify({
      clientId,
      pageSize,
      searchText,
      statusFilter,
      pageNumber,
      sortBy
    });
    const { data } = await axios.get<Order[]>(
      `/api/clientOrder/getorders?${queryString}`
    );
    if (!statusFilter && (data.length < pageSize || data.length + clientOrders.length === filterCount)) {
      setFilters({ ...filters, pageNumber: 1 })
      setSearchLegacy(true);
      getLegacyOrders(searchText, 1, pageSize, sortBy);
      getLegacyOrdersCount(searchText);
    }
    else {
      setSearchLegacy(false);
      setLegacyOrders([])
      setLegacyCount(0);
    }
    if (pageNumber > 1) {
      setClientOrders([...clientOrders, ...data]);
    }
    else {
      setClientOrders(data);
    }
  };

  const getLegacyOrders = async (
    searchText: string,
    pageNumber: number,
    pageSize: number,
    sortBy: string) => {
    const queryString = qs.stringify({ clientId, searchText, pageNumber, pageSize, sortBy });
    const { data } = await axios.get<Order[]>(
      `/api/clientorder/getlegacyOrders?${queryString}`
    );
    if (pageNumber > 1) {
      setLegacyOrders([...legacyOrders, ...data]);
    }
    else {
      setLegacyOrders(data);
    }
  }

  const getStatusCounts = async () => {
    const queryString = qs.stringify({
      clientId
    });
    const { data } = await axios.get<StatusCounts>(
      `/api/clientOrder/getStatusCounts?${queryString}`
    );
    setStatusCounts(data);
  };

  const getClientOrdersCount = async (
    searchText: string,
    statusFilter: string
  ) => {
    const queryString = qs.stringify({
      clientId,
      searchText,
      statusFilter,
    });
    const { data } = await axios.get<number>(
      `/api/clientOrder/getClientOrdersCount?${queryString}`
    );
    setTotal(data);
    setCount(data);
  };

  const getLegacyOrdersCount = async (searchText: string) => {
    const queryString = qs.stringify({
      clientId, searchText
    });
    const { data } = await axios.get<number>(
      `/api/clientOrder/getLegacyOrdersCount?${queryString}`
    );
    setLegacyCount(data);
    setLegacyTotal(data)
  }

  const getLastLegacyDate = async () => {
    const queryString = qs.stringify({ clientId });
    const { data } = await axios.get<Date>(`api/clientOrder/getLastLegacyDate?${queryString}`);
    setLastLegacyDate(data);
  }

  const getIfShowMore = () => {
    return clientOrders && filterCount > clientOrders?.length;
  };

  const getIfShowMoreLegacy = () => {
    if (filters.isFiltering) {
      return false;
    }
    if (legacyCount > 0) {
      return legacyOrders && legacyCount > legacyOrders?.length;
    } else {
      return legacyOrders && legacyTotal > legacyOrders?.length && legacyTotal !== 0;
    }
  };

  const closeSearch = () => {
    searchParams.setIsTopSearchOpen(false);
    searchParams.setMainSearchText('')
    search('');
  }

  const filter = async (status: FilterType) => {
    //set legacy?
    await getClientOrders(searchParams.mainSearchText, status, baseFilter.pageNumber, baseFilter.sortBy);
  };

  const headerSearch = async (searchText: string) => {
    if (searchText === '') {
      clear()
    }
    else {
      await Promise.all([
        getClientOrdersCount(searchText, baseFilter.statusFilter),
        getClientOrders(searchText, baseFilter.statusFilter, baseFilter.pageNumber, baseFilter.sortBy)
      ])
      setSearchFilterState(baseFilter.statusFilter, true, false);
      searchParams.setTopSearchText(searchText);
    }
  }

  const search = async (searchText: string) => {
    if (searchText === '') {
      clear();
    }
    else {
      searchParams.setMainSearchText(searchText);
      await Promise.all([
        getClientOrdersCount(searchText, baseFilter.statusFilter),
        getClientOrders(searchText, baseFilter.statusFilter, baseFilter.pageNumber, baseFilter.sortBy)
      ])
      searchParams.setMainSearchText(searchText);
      setSearchFilterState(baseFilter.statusFilter, true, false);
    }
  }

  const clear = async () => {
    setSearchFilterState(baseFilter.statusFilter, baseFilter.isSearching, baseFilter.isFiltering);
    searchParams.setMainSearchText('');
    await getClientOrders('', baseFilter.statusFilter, baseFilter.pageNumber, baseFilter.sortBy);
  };

  const showMore = async () => {
    const newFilters = { ...filters, pageNumber: filters.pageNumber + 1 }
    setFilters(newFilters);
    if (!searchLegacy) {
      await getClientOrders(searchParams.mainSearchText, newFilters.statusFilter, newFilters.pageNumber, newFilters.sortBy);
    }
    else {
      await getLegacyOrders(searchParams.mainSearchText, newFilters.pageNumber, pageSize, newFilters.sortBy);
    }
  };

  const sort = async (sortBy: string) => {
    setFilters({ ...baseFilter, sortBy: sortBy });
    await getClientOrders(searchParams.mainSearchText, baseFilter.statusFilter, baseFilter.pageNumber, sortBy);
  }

  const onFilterCardClick = async (filterType: FilterType, mapper: (counts: StatusCounts) => number) => {
    if (filters.statusFilter === filterType) {
      setSearchFilterState(baseFilter.statusFilter, baseFilter.isSearching, baseFilter.isFiltering);//, '', '', '', false);
      searchParams.setMainSearchText('');
      await filter('');
      setCount(total);
      setLegacyCount(legacyTotal);
      sessionStorage.removeItem('filter')
    } else {
      setSearchFilterState(filterType, false, true);
      searchParams.setMainSearchText('');
      await filter(filterType);
      statusCounts && setCount(mapper(statusCounts));
      sessionStorage.setItem('filter', filterType)
    }
    setLegacyOrders([])
    setSearchLegacy(false)
  }

  const setSearchFilterState = (status: FilterType, isSearching: boolean, isFiltering: boolean) => {
    setFilters({ ...baseFilter, statusFilter: status, isSearching: isSearching, isFiltering: isFiltering })
  }
  return (
    <div>
      <TopBar
        search={headerSearch}
        searchText={searchParams.topSearchText}
        searchOpen={!!searchParams.isTopSearchOpen}
        onSearchTextChange={searchParams.setTopSearchText}
        setSearchOpen={searchParams.setIsTopSearchOpen}
        closeSearch={closeSearch}
        info
        name={userContext.user.clientName}
      />
      <OuterWrap>
        <div>
          <HomeScreen>
            {filters.isFiltering && (
              <CardLayout>
                <Card
                  number={statusCounts?.inProgress}
                  status='In Progress'
                  onClick={async () => onFilterCardClick('In Progress', counts => counts.inProgress)}
                  selected={filters.statusFilter === 'In Progress'}
                />
                <Card
                  number={statusCounts?.partiallyCompleted}
                  status='Partially Completed'
                  onClick={async () => onFilterCardClick('Partially Completed', counts => counts.partiallyCompleted)}
                  selected={filters.statusFilter === 'Partially Completed'}
                />
                {!!statusCounts?.waitingOnClient && (
                  <Card
                    number={statusCounts?.waitingOnClient}
                    status='Waiting on Client'
                    onClick={async () => onFilterCardClick('Waiting on Client', counts => counts.waitingOnClient)}
                    selected={filters.statusFilter === 'Waiting on Client'}
                  />
                )}
                <Card
                  number={statusCounts?.completed}
                  status='Completed'
                  onClick={async () => onFilterCardClick('Completed', counts => counts.completed)}
                  selected={filters.statusFilter === 'Completed'}
                />
              </CardLayout>
            )}
            {filters.isSearching && (
              <Search
                search={search}
                searchText={searchParams.mainSearchText}
                onSearchTextChange={searchParams.setMainSearchText}
                closeSearch={clear}
              />
            )}
            {clientOrders && (
              <ClientOrders
                onClick={sort}
                orders={clientOrders}
                legacyOrders={legacyOrders}
                searchActive={filters.isSearching}
                filterActive={filters.isFiltering}
                onClickClear={clear}
                lastLegacyDate={lastLegacyDate}
                searchText={searchParams.mainSearchText}
              />
            )}
          </HomeScreen>
        </div>
      </OuterWrap>
      {(getIfShowMore() || getIfShowMoreLegacy()) &&
        <ButtonStyles>
          <Button
            buttonText='Show More'
            buttonType='ButtonTertiaryMedium'
            onClick={showMore}
            width='152px'
          />
        </ButtonStyles>
      }
    </div>
  );
};

export default HomePage;
